import { Navigate, type RouteObject } from 'react-router';
import { HasPermission } from '@/components/Permission/HasPermission/index.tsx';
import Unauthorized from '@/components/Unauthorized/index.tsx';
import { ApplicationDetails, ApplicationResources, ApplicationsList } from '@/features/ApplicationForm';
import ApplicationFormDetails from '@/features/ApplicationForm/Components/ApplicationFormDetails/index.tsx';
import { ApplicationFormReports } from '@/features/ApplicationForm/Components/ApplicationFormReports/index.tsx';
import { ApplicationFormReportsLayout } from '@/features/ApplicationForm/Components/ApplicationFormReports/Layout/index.tsx';
import { ReportDetails } from '@/features/ApplicationForm/Components/ApplicationFormReports/ReportDetails/index.tsx';
import { ReportSettings } from '@/features/ApplicationForm/Components/ApplicationFormReports/ReportSettings/index.tsx';
import { ImportApplications } from '@/features/ApplicationForm/Components/ImportApplications/index.tsx';
import { ApplicationFormDashboard } from '@/features/ApplicationForm/index.tsx';
import PageNotFound from '@/pages/PageNotFound/index.tsx';
import ApplicationForms from '@/pages/Rover/ApplicationForms';
import ClientApplication from '@/pages/Rover/ClientApplications/ClientApplication/index.tsx';
import ClientApplicationsDashboard from '@/pages/Rover/ClientApplications/ClientApplicationsDashboard.tsx';
import ClientApplications from '@/pages/Rover/ClientApplications/index.tsx';
import DueDiligence from '@/pages/Rover/DueDiligence';
import RoverDeliverable from '@/pages/Rover/DueDiligence/components/Deliverables/index.tsx';
import RoverMilestone from '@/pages/Rover/DueDiligence/components/Milestones/index.tsx';
import RoverTask from '@/pages/Rover/DueDiligence/components/Tasks/index.tsx';
import NewOpportunity from '@/pages/Rover/New/index.tsx';
import Opportunity from '@/pages/Rover/Opportunity/index.tsx';
import PartnerProposals from '@/pages/Rover/PartnerProposals/index.tsx';
import PartnerProposal from '@/pages/Rover/PartnerProposals/PartnerProposal/index.tsx';
import AllOpportunites from '@/pages/Rover/Tab/AllOpportunties/index.tsx';
import Overview from '@/pages/Rover/Tab/overview/index.tsx';
import { EPermission } from '@/types/Permission';
import type { HasPermissionFn } from '../../../hooks/usePermission';
import PartnerProposalsDashboard from '../../../pages/Rover/PartnerProposals/Dashboard';

export const RoverRoute = (hasPermission: HasPermissionFn) => {
  let applicationFormNav = ['dashboard', 'applications', 'resources', 'settings'];
  if (!hasPermission([EPermission.READ_APPLICATION_FORM_DASHBOARD])) {
    applicationFormNav = applicationFormNav.filter((n) => n !== 'dashboard');
  }
  if (!hasPermission([EPermission.LIST_APPLICATIONS])) {
    applicationFormNav = applicationFormNav.filter((n) => n !== 'applications');
  }
  if (!hasPermission([EPermission.LIST_APPLICATION_FORM_RESOURCES])) {
    applicationFormNav = applicationFormNav.filter((n) => n !== 'resources');
  }
  if (!hasPermission([EPermission.READ_APPLICATION_FORM])) {
    applicationFormNav = applicationFormNav.filter((n) => n !== 'settings');
  }

  return [
    {
      index: true,
      element: <Navigate to='application_forms' />,
    },
    {
      path: 'client-applications',
      children: [
        { index: true, element: <Navigate to='dashboard' /> },
        { path: 'dashboard', element: <ClientApplicationsDashboard /> },
        {
          path: 'list',
          element: (
            <HasPermission of='LIST_CLIENT_APPLICATIONS' fallback={<Unauthorized />}>
              <ClientApplications />
            </HasPermission>
          ),
        },
        {
          path: 'list/:id',
          element: (
            <HasPermission of='READ_CLIENT_APPLICATION' fallback={<Unauthorized />}>
              <ClientApplication />
            </HasPermission>
          ),
        },
      ],
    },
    {
      path: 'partner-proposals',
      children: [
        { index: true, element: <Navigate to='dashboard' /> },
        { path: 'dashboard', element: <PartnerProposalsDashboard /> },
        {
          path: 'list',
          element: (
            <HasPermission of='LIST_PARTNER_PROPOSALS' fallback={<Unauthorized />}>
              <PartnerProposals />
            </HasPermission>
          ),
        },
        {
          path: 'list/:id',
          element: (
            <HasPermission of='READ_PARTNER_PROPOSAL' fallback={<Unauthorized />}>
              <PartnerProposal />
            </HasPermission>
          ),
        },
      ],
    },
    {
      path: 'overView',
      element: <Overview />,
    },
    {
      path: 'opportunities',
      element: <AllOpportunites />,
    },
    {
      path: 'opportunities/new',
      element: <NewOpportunity />,
    },
    {
      path: 'opportunities/:opportunityId',
      element: <Opportunity />,
    },
    {
      path: 'application_forms',
      element: <ApplicationForms />,
    },
    {
      path: 'application_forms/:applicationFormId',
      children: [
        {
          index: true,
          element: <Navigate to={applicationFormNav[0] ?? 'dashboard'} replace />,
        },
        {
          path: 'dashboard',
          element: <ApplicationFormDashboard />,
        },
        {
          path: 'reports',
          element: <ApplicationFormReportsLayout />,
          children: [
            {
              index: true,
              element: <ApplicationFormReports />,
            },
            {
              path: ':reportId',
              element: <ReportDetails />,
            },
            {
              path: ':reportId/settings',
              element: <ReportSettings />,
            },
          ],
        },
        {
          path: 'applications',
          element: <ApplicationsList />,
        },
        {
          path: 'applications/:applicationId',
          children: [
            {
              index: true,
              element: <Navigate to='application_details' />,
            },
            {
              path: 'application_details',
              element: <ApplicationDetails />,
            },
            {
              path: 'due_diligence',
              element: <DueDiligence />,
            },
            {
              path: 'due_diligence/task/:taskId',
              element: <RoverTask />,
            },
            {
              path: 'due_diligence/deliverable/:deliverableId',
              element: <RoverDeliverable />,
            },
            {
              path: 'due_diligence/milestone/:milestoneId',
              element: <RoverMilestone />,
            },
          ],
        },
        {
          path: 'resources',
          element: <ApplicationResources />,
        },
        {
          path: 'settings',
          element: <ApplicationFormDetails />,
        },
        {
          path: 'import_applications',
          element: <ImportApplications />,
        },
      ],
    },
    {
      path: '*',
      element: <PageNotFound />,
    },
  ] as RouteObject[];
};
